import { FC, useState, useEffect, useCallback, PropsWithChildren } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ErrorConfig, ERROR_CODES } from '@/lib/http-client';
import { getErrorDisplayMessage, isErrorConfig, shouldRedirectAskHackettAuthorization } from '@/lib/utils';
import { ErrorDisplay, Loader } from '@/components';
import { useAlert, useSignOut } from '@/hooks';

enum DISPLAY_STATES {
	LOADING = 'LOADING',
	SUCCESS = 'SUCCESS',
	ERROR = 'ERROR',
}

interface AsyncPageProps {
	fetchData(): Promise<void>;
	abortFetch?(): void;
	errorDisplayClassName?: string;
	loader?: JSX.Element;
	errorElement?: JSX.Element;
	onError?(error: ErrorConfig | unknown): void;
}

export const AsyncPage: FC<PropsWithChildren<AsyncPageProps>> = ({
	children,
	fetchData,
	abortFetch,
	errorDisplayClassName,
	loader,
	errorElement,
	onError,
}) => {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { signOutNoServiceCall } = useSignOut();
	const { showAlert } = useAlert();
	const [fetchPageDataError, setFetchPageDataError] = useState<ErrorConfig | unknown>(undefined);
	const [displayState, setDisplayState] = useState(DISPLAY_STATES.LOADING);

	const fetchPageData = useCallback(async () => {
		setDisplayState(DISPLAY_STATES.LOADING);
		setFetchPageDataError(undefined);

		try {
			await fetchData();
			setDisplayState(DISPLAY_STATES.SUCCESS);
		} catch (error) {
			if (isErrorConfig(error)) {
				if (error.code === ERROR_CODES.REQUEST_ABORTED) {
					return;
				}

				if (error.status === 503) {
					navigate('/maintenance', { replace: true });
					return;
				}

				if (error.code === 'AUTHENTICATION_REQUIRED' || error.code === 'NotAuthorizedException') {
					signOutNoServiceCall();
					return;
				}

				if (error.status === 401) {
					signOutNoServiceCall();
					return;
				}

				if (error.status === 403) {
					if (shouldRedirectAskHackettAuthorization(error, pathname)) {
						showAlert({
							variant: 'danger',
							children: (
								<p className="mb-0 text-white" style={{ whiteSpace: 'pre-line' }}>
									{getErrorDisplayMessage(error)}
								</p>
							),
						});
						navigate('/', { replace: true });
						return;
					}
				}
			}

			onError?.(error);
			setFetchPageDataError(error);
			setDisplayState(DISPLAY_STATES.ERROR);
		}
	}, [fetchData, navigate, onError, pathname, showAlert, signOutNoServiceCall]);

	useEffect(() => {
		fetchPageData();

		return () => {
			typeof abortFetch === 'function' && abortFetch();
		};
	}, [fetchPageData, abortFetch]);

	function getDisplayState() {
		switch (displayState) {
			case DISPLAY_STATES.LOADING:
				if (loader) {
					return loader;
				}

				return (
					<div
						data-testid="async-page__loader"
						className="pt-20 d-flex justify-content-center align-items-center"
					>
						<Loader />
					</div>
				);
			case DISPLAY_STATES.SUCCESS:
				return children;
			case DISPLAY_STATES.ERROR:
				if (errorElement) {
					return errorElement;
				}

				return (
					<ErrorDisplay
						className={errorDisplayClassName}
						error={fetchPageDataError}
						showBackButton={true}
						showRetryButton={!(isErrorConfig(fetchPageDataError) && fetchPageDataError.status === 403)}
						onRetryButtonClick={fetchPageData}
					/>
				);
			default:
				return <></>;
		}
	}

	return <>{getDisplayState()}</>;
};
